import React, { useState, Fragment } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Formik, Form, Field, FieldArray } from 'formik';
import axios from 'axios';
import CustomLoader from '../../../common/CustomLoader';
import showAlert from '../../../../common/utils/alert';
import { builderSurfaceJsonContentHeader } from '../../../../common/utils/restApiHeaderUtils';

export const ListMappings = ({ collections, actions, commonActions, projectId, version }) => {
  const {
    metaDataTableMappings,
    deleteCollectionMetaDataTableMappingPending,
    selectedCollection,
    projectCollections,
  } = collections;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [targetCollectionFields, setTargetCollectionFields] = useState([]);
  const [referenceFields, setReferenceFields] = useState([]);
  const [referenceFieldsMap, setReferenceFieldsMap] = useState({});
  const [referenceCollectionNamesMap, setReferenceCollectionNamesMap] = useState({});

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleEdit = async (mapping) => {
    setSelectedMapping(mapping);
    await targetCollectionData(mapping.targetCollection);
    const newReferenceFieldsMap = {};
    const newReferenceCollectionNamesMap = {};
    const newMappings = mapping.mappings.map((map, index) => {
      let refFields = [];
      let initialRefFieldValue = '';
      let refCollectionName = map.referenceCollectionName || '';
      if (refCollectionName) {
        const referenceCollection = projectCollections.find(
          (collection) => collection.collectionName === refCollectionName,
        );

        if (referenceCollection) {
          refFields = referenceCollection.fields.filter(
            (field) => field.fieldName !== 'primary_key',
          );
          newReferenceFieldsMap[index] = refFields;
          newReferenceCollectionNamesMap[index] = refCollectionName;

          if (
            map.referenceCollectionField &&
            refFields.find((field) => field.fieldName === map.referenceCollectionField)
          ) {
            initialRefFieldValue = map.referenceCollectionField;
          }
        }
      }
      return {
        ...map,
        referenceCollectionField: initialRefFieldValue,
        referenceCollectionName: refCollectionName,
      };
    });
    setReferenceFieldsMap(newReferenceFieldsMap);
    setReferenceCollectionNamesMap(newReferenceCollectionNamesMap);
    setSelectedMapping((prev) => ({
      ...prev,
      mappings: newMappings,
    }));
    setIsModalOpen(true);
  };

  const handleDelete = (mappingId) => {
    const { selectedCollection } = collections;
    const preConfirm = () => {
      actions.deleteCollectionMetaDataTableMapping({
        projectId,
        version,
        mappingId,
        selectedCollection,
      });
      commonActions.trackCollectionChange({
        uuid: selectedCollection.uuid,
        name: selectedCollection.collectionName,
      });
    };
    showAlert("You won't be able to revert this.", preConfirm, 'Yes, delete it!');
  };

  const handleUpdate = (mappingId, values) => {
    const { selectedCollection } = collections;
    const enrichedMappings = values.mappings
      .filter(
        (mapping) =>
          mapping.metaDataField.trim() !== '' || mapping.targetCollectionField.trim() !== '',
      )
      .map((mapping, index) => ({
        ...mapping,
        referenceCollectionName: referenceCollectionNamesMap[index] || null,
      }));

    const updatedValues = {
      ...values,
      mappings: enrichedMappings,
    };
    const preConfirm = () => {
      actions.updateCollectionMetaDataTableMapping({
        projectId,
        version,
        mappingId,
        selectedCollection,
        updatedValues,
      });
      toggleModal();
    };
    showAlert(
      'Updating this will require reindexing the data. Do you want to proceed?',
      preConfirm,
      'Yes, update it!',
      'info',
    );
  };

  const targetCollectionData = async (targetCollectionId) => {
    try {
      const uri = `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/collections/${targetCollectionId}/`;
      const header = builderSurfaceJsonContentHeader(projectId);
      await axios.get(uri, header).then((res) => {
        setTargetCollectionFields(res.data.fields);
        setReferenceFields(res.data.fields.filter((field) => field.type === 'reference'));
      });
    } catch (error) {
      console.error('Error fetching collection details:', error);
    }
  };

  const handleFieldChange = (fieldName, index) => {
    const refFieldDetails = referenceFields.find((refField) => refField.fieldName === fieldName);
    if (
      refFieldDetails &&
      refFieldDetails.refCollection &&
      refFieldDetails.refCollection.collectionName
    ) {
      const collectionName = refFieldDetails.refCollection.collectionName;
      const referenceCollection = projectCollections.find(
        (collection) => collection.collectionName === collectionName,
      );

      if (referenceCollection) {
        const fields = referenceCollection.fields.filter(
          (field) => field.fieldName !== 'primary_key',
        );
        setReferenceFieldsMap((prev) => ({
          ...prev,
          [index]: fields,
        }));
        setReferenceCollectionNamesMap((prev) => ({
          ...prev,
          [index]: collectionName,
        }));
      }
    } else {
      setReferenceFieldsMap((prev) => ({
        ...prev,
        [index]: [],
      }));

      setReferenceCollectionNamesMap((prev) => ({
        ...prev,
        [index]: null,
      }));
    }
  };

  if (metaDataTableMappings && metaDataTableMappings.length > 0) {
    return (
      <>
        <Accordion allowZeroExpanded>
          {metaDataTableMappings.map((metaMapping, index) => (
            <AccordionItem key={index}>
              <AccordionItemHeading>
                <AccordionItemButton>
                  <i className={`wb`}></i> <strong>{metaMapping.mappingName}</strong>
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <Fragment>
                  <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12 drapcode">
                    <h5>
                      <strong>Name:</strong> {metaMapping.mappingName}
                    </h5>
                    <h5>
                      <strong>Primary Key:</strong> {metaMapping.primaryKey}
                    </h5>
                    <h5>
                      <strong>Target Collection:</strong> {metaMapping.targetCollection}
                    </h5>
                    <br />
                    <div className="card">
                      <div
                        className="card-header card-header-text"
                        data-background-color="drapcode-violet"
                      >
                        <h6 className="card-title pb-0 mb-0 text-capitalise">
                          {metaMapping.mappingName}
                        </h6>
                      </div>
                      <div className="card-content">
                        <div className="row mx-auto">
                          <div className="table-responsive">
                            <table className="table table-grey table-bordered connector-list">
                              <thead>
                                <tr>
                                  <th>Meta Data Fields</th>
                                  <th>Target Collection Fields</th>
                                  <th>Reference Fields</th>
                                </tr>
                              </thead>
                              <tbody>
                                {metaMapping.mappings && metaMapping.mappings.length > 0
                                  ? metaMapping.mappings.map((mapping) => (
                                      <tr key={mapping.uuid}>
                                        <td>{mapping.metaDataField}</td>
                                        <td>{mapping.targetCollectionField}</td>
                                        <td>{mapping.referenceCollectionField || '-'}</td>
                                      </tr>
                                    ))
                                  : ''}
                              </tbody>
                            </table>
                          </div>
                          <div className="col-12 text-right my-2 px-0">
                            <button
                              type="button"
                              className="btn btn-sm btn-theme-submit"
                              onClick={() => handleEdit(metaMapping)}
                              disabled={deleteCollectionMetaDataTableMappingPending}
                            >
                              <i className="wb webconnect-pencil"></i> Edit&nbsp;
                              <CustomLoader loading={deleteCollectionMetaDataTableMappingPending} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-theme-delete"
                              onClick={() => handleDelete(metaMapping.uuid)}
                              disabled={deleteCollectionMetaDataTableMappingPending}
                            >
                              <i className="wb webconnect-trash-regular"></i> Delete&nbsp;
                              <CustomLoader loading={deleteCollectionMetaDataTableMappingPending} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Fragment>
              </AccordionItemPanel>
            </AccordionItem>
          ))}
        </Accordion>

        {selectedMapping && (
          <Modal isOpen={isModalOpen} toggle={toggleModal} backdrop="static">
            <ModalHeader toggle={toggleModal}>Edit Mapping</ModalHeader>
            <Formik
              initialValues={{
                mappingName: selectedMapping.mappingName || '',
                targetCollection: selectedMapping.targetCollection || '',
                mappings: [
                  ...selectedMapping.mappings,
                  {
                    metaDataField: '',
                    targetCollectionField: '',
                    referenceCollectionName: '',
                    referenceCollectionField: '',
                  },
                ],
                primaryKey: selectedMapping.primaryKey || '',
                uuid: selectedMapping.uuid || '',
              }}
              onSubmit={(values) => handleUpdate(selectedMapping.uuid, values)}
              enableReinitialize
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <ModalBody>
                    <div className="mb-3">
                      <label>Mapping Name</label>
                      <Field className="form-control" name="mappingName" />
                    </div>
                    <div className="mb-3">
                      <label>Target Collection</label>
                      <Field className="form-control" name="targetCollection" disabled />
                    </div>
                    <div className="mb-3">
                      <label>Primary Key</label>
                      <Field className="form-control" name="primaryKey" disabled />
                    </div>
                    <FieldArray name="mappings">
                      {({ push, remove }) => (
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Meta Data Field</th>
                              <th>Target Collection Field</th>
                              <th>Reference Field</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {values.mappings.map((mapping, index) => (
                              <tr key={index}>
                                <td>
                                  <Field
                                    as="select"
                                    className="form-control"
                                    name={`mappings[${index}].metaDataField`}
                                    onChange={(e) => {
                                      const selectedValue = e.target.value;
                                      setFieldValue(
                                        `mappings[${index}].metaDataField`,
                                        selectedValue,
                                      );
                                      if (index === values.mappings.length - 1 && selectedValue) {
                                        push({
                                          metaDataField: '',
                                          targetCollectionField: '',
                                          referenceCollectionName: '',
                                          referenceCollectionField: '',
                                        });
                                      }
                                    }}
                                  >
                                    <option value="">Select Meta Data Field</option>
                                    {selectedCollection.fields.map((field) => (
                                      <option key={field.fieldName} value={field.fieldName}>
                                        {field.fieldTitle.en}
                                      </option>
                                    ))}
                                  </Field>
                                </td>
                                <td>
                                  <Field
                                    as="select"
                                    className="form-control"
                                    name={`mappings[${index}].targetCollectionField`}
                                    disabled={
                                      !targetCollectionFields || targetCollectionFields.length === 0
                                    }
                                    onChange={(e) => {
                                      const selectedValue = e.target.value;
                                      setFieldValue(
                                        `mappings[${index}].targetCollectionField`,
                                        selectedValue,
                                      );
                                      handleFieldChange(selectedValue, index); // pass index here
                                    }}
                                  >
                                    <option value="">Select Target Field</option>
                                    {targetCollectionFields.map((field) => (
                                      <option key={field.fieldName} value={field.fieldName}>
                                        {field.fieldTitle.en}
                                      </option>
                                    ))}
                                  </Field>
                                </td>
                                <td>
                                  <Field
                                    as="select"
                                    className="form-control"
                                    name={`mappings[${index}].referenceCollectionField`}
                                  >
                                    <option value="">Select Reference Field</option>
                                    {referenceFieldsMap[index] &&
                                      referenceFieldsMap[index].map((field) => (
                                        <option key={field.fieldName} value={field.fieldName}>
                                          {field.fieldTitle.en}
                                        </option>
                                      ))}
                                  </Field>
                                </td>
                                <td>
                                  {values.mappings.length > 1 &&
                                    index !== values.mappings.length - 1 && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => remove(index)}
                                      >
                                        X
                                      </button>
                                    )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </FieldArray>
                  </ModalBody>
                  <ModalFooter
                    className="d-flex justify-content-between"
                    style={{ paddingBottom: '0px' }}
                  >
                    <button
                      type="button"
                      className="btn btn-xs btn-theme-default-outlined"
                      onClick={toggleModal}
                    >
                      <span>
                        <i className={`wb webconnect-times`}></i>&nbsp;Cancel
                      </span>
                    </button>
                    <button type="submit" className="btn btn-xs btn-theme-submit">
                      <span>
                        <i className={`wb webconnect-save-regular`}></i>&nbsp;Update
                      </span>
                    </button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </Modal>
        )}
      </>
    );
  } else {
    return <h5 className="text-center">No Mappings!</h5>;
  }
};
